import {
  defaultLoadableState,
  useLoadableState
} from '@carvana/merch-react-hooks';
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react';
import type { OwnerReview, VehicleRating } from '~/contracts/vdp';

type DrawerOptions = {
  isInputAnchored: boolean;
  isInputFocused: boolean;
};

type ContextType = {
  drawerOptions: DrawerOptions;
  drawerReviewCount: number;
  drawerReviews: ReturnType<typeof useLoadableState<OwnerReview[]>>;
  hasMoreReviews: boolean;
  hasOwnerReviews: ReturnType<typeof useLoadableState<boolean>>;
  pageNumber: number;
  pageReviewCount: number;
  pageReviews: ReturnType<typeof useLoadableState<OwnerReview[]>>;
  ratingData: ReturnType<typeof useLoadableState<VehicleRating>>;
  resetDrawerOptions: () => void;
  searchTerm: string;
  selectedReview: string | undefined;
  setDrawerOptions: (options: DrawerOptions) => void;
  setDrawerReviewCount: (count: number) => void;
  setPageNumber: (page: number) => void;
  setPageReviewCount: (count: number) => void;
  setSearchTerm: (term: string) => void;
  setSelectedReviewId: (id?: string) => void;
  setStarRatingFilter: (rating?: number) => void;
  starRatingFilter: number | undefined;
};

const defaultContext: ContextType = {
  drawerOptions: {
    isInputAnchored: false,
    isInputFocused: false
  },
  drawerReviewCount: 0,
  drawerReviews: defaultLoadableState<OwnerReview[]>([]),
  hasMoreReviews: false,
  hasOwnerReviews: defaultLoadableState<boolean>(false),
  pageNumber: 1,
  pageReviewCount: 0,
  pageReviews: defaultLoadableState<OwnerReview[]>([]),
  ratingData: defaultLoadableState<VehicleRating>({
    ratingDistribution: [],
    totalRatingCounts: 0
  }),
  resetDrawerOptions: () => {},
  searchTerm: '',
  selectedReview: undefined as string | undefined,
  setDrawerOptions: (_: DrawerOptions) => {},
  setDrawerReviewCount: (_: number) => {},
  setPageNumber: (_: number) => {},
  setPageReviewCount: (_: number) => {},
  setSearchTerm: (_: string) => {},
  setSelectedReviewId: (_?: string) => {},
  setStarRatingFilter: (_: number | undefined) => {},
  starRatingFilter: undefined as number | undefined
};

const StarRatingOwnerReviewContext = createContext<ContextType>(defaultContext);

export function StarRatingOwnerReviewContextProvider({
  children
}: React.PropsWithChildren) {
  const [drawerReviewCount, setDrawerReviewCount] = useState(
    defaultContext.drawerReviewCount
  );
  const hasOwnerReviews = useLoadableState<boolean>(
    defaultContext.hasOwnerReviews.data
  );
  const drawerReviews = useLoadableState<OwnerReview[]>([]);
  const pageReviews = useLoadableState<OwnerReview[]>([]);
  const ratingData = useLoadableState<VehicleRating>({
    ratingDistribution: [],
    totalRatingCounts: 0
  });
  const [pageNumber, setPageNumber] = useState(defaultContext.pageNumber);
  const [pageReviewCount, setPageReviewCount] = useState(
    defaultContext.pageReviewCount
  );
  const [searchTerm, setSearchTerm] = useState(defaultContext.searchTerm);
  const [starRatingFilter, setStarRatingFilter] = useState(
    defaultContext.starRatingFilter
  );
  const [selectedReview, setSelectedReviewId] = useState(
    defaultContext.selectedReview
  );
  const [drawerOptions, setDrawerOptions] = useState<DrawerOptions>(
    defaultContext.drawerOptions
  );

  const resetDrawerOptions = useCallback(() => {
    setDrawerOptions(defaultContext.drawerOptions);
  }, []);

  const value = useMemo<ContextType>(
    () => ({
      drawerOptions,
      drawerReviewCount,
      drawerReviews,
      hasMoreReviews: drawerReviews.data.length < drawerReviewCount,
      hasOwnerReviews,
      pageNumber,
      pageReviewCount,
      pageReviews,
      ratingData,
      resetDrawerOptions,
      searchTerm,
      selectedReview,
      setDrawerOptions,
      setDrawerReviewCount,
      setPageNumber,
      setPageReviewCount,
      setSearchTerm,
      setSelectedReviewId,
      setStarRatingFilter,
      starRatingFilter
    }),
    [
      drawerOptions,
      drawerReviewCount,
      drawerReviews,
      hasOwnerReviews,
      pageNumber,
      pageReviewCount,
      pageReviews,
      ratingData,
      resetDrawerOptions,
      searchTerm,
      selectedReview,
      starRatingFilter
    ]
  );

  return (
    <StarRatingOwnerReviewContext.Provider value={value}>
      {children}
    </StarRatingOwnerReviewContext.Provider>
  );
}

StarRatingOwnerReviewContextProvider.displayName =
  'StarRatingOwnerReviewContextProvider';

export function useHasOwnerReviews() {
  const { hasOwnerReviews } = useContext(StarRatingOwnerReviewContext);

  return hasOwnerReviews.data;
}

export default StarRatingOwnerReviewContext;
